import { Injectable, computed, signal } from '@angular/core';

import { RegisteredArticleWarning } from '@shared-models';

/**
 * Live registry of article warnings currently visible on screen. A
 * "reporter" (`InventoryArticleWarningComponent`) registers on mount when it
 * has a warning and unregisters on destroy or when the warning clears — so
 * `entries()` always reflects only what's currently mounted, never a
 * history of everything seen in the session.
 */
@Injectable({ providedIn: 'root' })
export class InventoryWarningNotificationStoreService {
  private nextToken = 0;
  private readonly registrations = signal<
    Map<number, RegisteredArticleWarning>
  >(new Map());

  /**
   * Deduplicated by `articleId` — the same article can be registered by more
   * than one on-screen row (rare, but possible), and should still surface as
   * a single entry in the notification.
   */
  readonly entries = computed<RegisteredArticleWarning[]>(() => {
    const byArticleId = new Map<number, RegisteredArticleWarning>();
    for (const entry of this.registrations().values()) {
      byArticleId.set(entry.articleId, entry);
    }
    return Array.from(byArticleId.values());
  });

  /** Returns an opaque token; pass it back to `unregister` to remove this entry. */
  register(entry: RegisteredArticleWarning): number {
    const token = this.nextToken++;
    const next = new Map(this.registrations());
    next.set(token, entry);
    this.registrations.set(next);
    return token;
  }

  unregister(token: number): void {
    const current = this.registrations();
    if (!current.has(token)) {
      return;
    }
    const next = new Map(current);
    next.delete(token);
    this.registrations.set(next);
  }
}
